import { useState, useEffect, useCallback, useRef } from "react";
import EmptyState from "./EmptyState";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const authFetch = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (v) => "₹" + Number(v||0).toLocaleString("en-IN");

const PLATFORMS = [
  { id:"shopify",  name:"Shopify",  color:"#5a8a00", bg:"#f0fdf4" },
  { id:"amazon",   name:"Amazon",   color:"#c45000", bg:"#fff3e0" },
  { id:"flipkart", name:"Flipkart", color:"#1b57c0", bg:"#eff6ff" },
  { id:"myntra",   name:"Myntra",   color:"#b5003c", bg:"#fff0f5" },
  { id:"meesho",   name:"Meesho",   color:"#9f2089", bg:"#fdf4ff" },
];

const TABS = [
  { id:"overview",  label:"📊 Overview"  },
  { id:"products",  label:"📦 Products"  },
  { id:"warehouse", label:"🏭 Warehouse" },
  { id:"reorder",   label:"🤖 AI Reorder"},
  { id:"skumap",    label:"🔗 SKU Map"   },
];

function stockStatus(inv, alert = 5) {
  const total = Object.values(inv||{}).reduce((a,b) => a + (b||0), 0);
  if (total === 0)         return { label:"Out of Stock", color:"#dc2626", bg:"#fef2f2" };
  if (total <= (alert||5)) return { label:"Low Stock",    color:"#d97706", bg:"#fffbeb" };
  return                          { label:"In Stock",     color:"#15803d", bg:"#f0fdf4" };
}

function totalStock(inv) {
  return Object.values(inv||{}).reduce((a,b) => a + (b||0), 0);
}

export default function PageInventoryPro({ toast, products=[], onUpdateInventory, onSync, syncStatus={}, locationId }) {
  const [tab,        setTab]       = useState("overview");
  const [search,     setSearch]    = useState("");
  const [filterPlat, setFilterPlat]= useState("all");
  const [filterStat, setFilterStat]= useState("all");
  const [sortBy,     setSortBy]    = useState("name");
  const [loading,    setLoading]   = useState(false);
  const [aiLoading,  setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [skuMap,     setSkuMap]    = useState(() => {
    try { return JSON.parse(localStorage.getItem("sellsync_skumap") || "{}"); } catch { return {}; }
  });
  const [skuEdits,   setSkuEdits]  = useState({});
  const [bulkMode,   setBulkMode]  = useState(false);
  const [selected,   setSelected]  = useState([]);
  const [editingProd,setEditingProd] = useState(null);

  // Push stock to Shopify
  const pushToShopify = async (product) => {
    if (!product.shopifyId) { toast("No Shopify ID for this product","error"); return; }
    const qty = product.inventory?.shopify ?? 0;
    try {
      // Get location ID first
      const locData = await authFetch("/shopify/locations");
      const locationId = locData.locations?.[0]?.id;
      if (!locationId) { toast("Could not get Shopify location","error"); return; }
      // Get inventory item ID from product variants
      const prodData = await authFetch(`/products/${product.shopifyId}`);
      const inventoryItemId = prodData.product?.variants?.[0]?.inventory_item_id;
      if (!inventoryItemId) { toast("Could not get inventory item ID","error"); return; }
      await authFetch("/inventory/update", {
        method: "POST",
        body: JSON.stringify({ inventory_item_id:inventoryItemId, location_id:locationId, available:qty }),
      });
      toast(`✅ Pushed ${qty} units to Shopify for ${product.name}`, "success");
    } catch(e) { toast("Push failed: " + e.message, "error"); }
  };

  const pushAllToShopify = async () => {
    if (!window.confirm(`Push inventory for ${filtered.length} products to Shopify?`)) return;
    let success = 0;
    for (const p of filtered) {
      try { await pushToShopify(p); success++; } catch(e) { /* skip */ }
    }
    toast(`✅ Pushed ${success}/${filtered.length} products to Shopify`, "success");
  };

  // Filtered + sorted products
  const filtered = products.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.shopifySku?.toLowerCase().includes(q);
    const matchPlat   = filterPlat === "all" || (p.inventory?.[filterPlat] ?? 0) >= 0;
    const st          = stockStatus(p.inventory, p.lowAlert);
    const matchStat   = filterStat === "all" ||
      (filterStat === "out"  && st.label === "Out of Stock") ||
      (filterStat === "low"  && st.label === "Low Stock") ||
      (filterStat === "ok"   && st.label === "In Stock");
    return matchSearch && matchPlat && matchStat;
  }).sort((a, b) => {
    if (sortBy === "name")  return (a.name||"").localeCompare(b.name||"");
    if (sortBy === "stock") return totalStock(b.inventory) - totalStock(a.inventory);
    if (sortBy === "price") return (b.price||0) - (a.price||0);
    if (sortBy === "alert") return totalStock(a.inventory) - totalStock(b.inventory);
    return 0;
  });

  // Overview stats
  const totalProducts  = products.length;
  const inStock        = products.filter(p => totalStock(p.inventory) > (p.lowAlert||5)).length;
  const lowStock       = products.filter(p => { const t = totalStock(p.inventory); return t > 0 && t <= (p.lowAlert||5); }).length;
  const outOfStock     = products.filter(p => totalStock(p.inventory) === 0).length;
  const totalValue     = products.reduce((a, p) => a + (p.price||0) * totalStock(p.inventory), 0);
  const platformTotals = PLATFORMS.map(pl => ({
    ...pl,
    total: products.reduce((a, p) => a + (p.inventory?.[pl.id]||0), 0),
  }));

  const updateInv = (productId, platformId, value) => {
    const qty = parseInt(value) || 0;
    if (onUpdateInventory) onUpdateInventory(productId, platformId, qty);
  };

  const bulkSetAll = (qty) => {
    if (!qty) return;
    selected.forEach(id => {
      PLATFORMS.forEach(pl => updateInv(id, pl.id, qty));
    });
    toast(`Set ${selected.length} products to ${qty} on all platforms`, "success");
    setSelected([]);
    setBulkMode(false);
  };

  const generateReorder = async () => {
    setAiLoading(true);
    try {
      const lowItems = products.filter(p => totalStock(p.inventory) <= (p.lowAlert||5) * 2);
      const data = await authFetch("/ai/text", {
        method: "POST",
        body: JSON.stringify({
          prompt: `You are an inventory analyst for an Indian ecommerce leather belts brand.
Analyze these products and suggest reorder quantities:

${lowItems.slice(0,15).map(p => `- ${p.name} (SKU: ${p.shopifySku}) — Current stock: ${totalStock(p.inventory)} units across platforms. Low alert: ${p.lowAlert||5}`).join('\n')}

For each product provide:
1. Suggested reorder quantity (based on typical 30-day buffer)
2. Priority (High/Medium/Low)
3. Reason in one line

Return as JSON array: [{"sku":"...","name":"...","reorderQty":50,"priority":"High","reason":"..."}]
Return ONLY the JSON array, no other text.`,
          maxTokens: 1000,
        }),
      });
      const text  = (data.result||"").replace(/```json|```/g,"").trim();
      const suggestions = JSON.parse(text);
      setAiSuggestions(suggestions);
      toast("✅ AI reorder suggestions ready", "success");
    } catch(e) {
      toast("AI error: "+e.message, "error");
    }
    setAiLoading(false);
  };

  const saveSkuMap = (shopifySku) => {
    const edits  = skuEdits[shopifySku] || {};
    const updated = { ...skuMap, [shopifySku]: { ...(skuMap[shopifySku]||{}), ...edits } };
    setSkuMap(updated);
    localStorage.setItem("sellsync_skumap", JSON.stringify(updated));
    setSkuEdits(prev => { const n={...prev}; delete n[shopifySku]; return n; });
    toast("SKU mapping saved ✓", "success");
  };

  const S = {
    card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
    hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
    th:     { padding:"8px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" },
    td:     { padding:"7px 12px", fontSize:12, borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
    btn:    (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
    btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
    input:  { padding:"6px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" },
    qtyInput: (qty, alert) => ({
      width:60, textAlign:"center", padding:"4px 6px", borderRadius:6, fontSize:12, fontFamily:"inherit", outline:"none",
      border:"1px solid " + (qty===0?"#fecaca":qty<=(alert||5)?"#fde68a":"#d1d5db"),
      background: qty===0?"#fef2f2":qty<=(alert||5)?"#fffbeb":"#fff",
      fontWeight:600,
    }),
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>📦 Inventory Pro</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Real-time stock across all platforms</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {/* Sync status */}
          <div style={{ display:"flex", gap:6 }}>
            {PLATFORMS.slice(0,3).map(pl => (
              <div key={pl.id} style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 8px", borderRadius:12, background:pl.bg, fontSize:10, fontWeight:600, color:pl.color }}>
                <div style={{ width:5, height:5, borderRadius:"50%", background:syncStatus[pl.id]==="ok"?"#22c55e":syncStatus[pl.id]==="syncing"?"#f59e0b":"#9ca3af" }}/>
                {pl.name}
              </div>
            ))}
          </div>
          <button style={S.btn("#2563eb")} onClick={onSync}>↻ Sync All</button>
          <button style={S.btn()} onClick={() => setBulkMode(!bulkMode)}>
            {bulkMode ? "✕ Cancel Bulk" : "✦ Bulk Edit"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:14 }}>
        {TABS.map(t => (
          <button key={t.id} style={{ padding:"9px 16px", border:"none", borderBottom:tab===t.id?"2px solid #F47920":"2px solid transparent", background:"none", color:tab===t.id?"#F47920":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
            onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ OVERVIEW ══ */}
      {tab === "overview" && (
        <div>
          {/* KPI row */}
          <div className="kpi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
            {[
              { label:"Total Products", value:totalProducts,     color:"#2563eb", icon:"📦" },
              { label:"In Stock",       value:inStock,           color:"#15803d", icon:"✅" },
              { label:"Low Stock",      value:lowStock,          color:"#d97706", icon:"⚠️" },
              { label:"Out of Stock",   value:outOfStock,        color:"#dc2626", icon:"❌" },
              { label:"Inventory Value",value:INR(totalValue),   color:"#7e22ce", icon:"💰" },
            ].map(k => (
              <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"12px 14px", borderLeft:"3px solid "+k.color }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px", marginBottom:3 }}>{k.label}</div>
                <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Platform breakdown */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <div style={S.card}>
              <div style={S.hdr}><span>📊 Stock by Platform</span></div>
              <div style={{ padding:"8px 0" }}>
                {platformTotals.map(pl => {
                  const maxTotal = Math.max(...platformTotals.map(p => p.total), 1);
                  return (
                    <div key={pl.id} style={{ padding:"7px 16px", display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:68, fontSize:11, fontWeight:600, color:pl.color, flexShrink:0 }}>{pl.name}</div>
                      <div style={{ flex:1, height:8, background:"#f3f4f6", borderRadius:4, overflow:"hidden" }}>
                        <div style={{ height:8, background:pl.color, width:Math.min(100,(pl.total/maxTotal)*100)+"%", borderRadius:4, transition:"width .4s" }}/>
                      </div>
                      <div style={{ width:50, textAlign:"right", fontWeight:700, fontSize:12, color:pl.color }}>{pl.total.toLocaleString("en-IN")}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>⚠️ Needs Attention</span></div>
              <div style={{ overflowY:"auto", maxHeight:200 }}>
                {products.filter(p => totalStock(p.inventory) <= (p.lowAlert||5)*2).slice(0,8).map(p => {
                  const st = stockStatus(p.inventory, p.lowAlert);
                  return (
                    <div key={p.id} style={{ padding:"8px 16px", borderBottom:"1px solid #f9fafb", display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                        <div style={{ fontSize:10, color:"#9ca3af" }}>{p.shopifySku} · {totalStock(p.inventory)} units total</div>
                      </div>
                      <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:st.bg, color:st.color, fontWeight:700, flexShrink:0 }}>{st.label}</span>
                    </div>
                  );
                })}
                {products.filter(p => totalStock(p.inventory) <= (p.lowAlert||5)*2).length === 0 && (
                  <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:12 }}>✅ All products well stocked</div>
                )}
              </div>
            </div>
          </div>

          {/* Recent activity placeholder */}
          <div style={S.card}>
            <div style={S.hdr}><span>📈 Stock Distribution</span></div>
            <div style={{ padding:16, display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
              {[
                { label:"Healthy (>10 units)", value:products.filter(p=>totalStock(p.inventory)>10).length, color:"#15803d", bg:"#f0fdf4" },
                { label:"Low (1-10 units)",    value:products.filter(p=>{const t=totalStock(p.inventory);return t>0&&t<=10;}).length, color:"#d97706", bg:"#fffbeb" },
                { label:"Zero stock",          value:products.filter(p=>totalStock(p.inventory)===0).length, color:"#dc2626", bg:"#fef2f2" },
              ].map(s => (
                <div key={s.label} style={{ background:s.bg, borderRadius:8, padding:"14px 16px", textAlign:"center" }}>
                  <div style={{ fontSize:28, fontWeight:700, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:11, color:s.color, marginTop:4 }}>{s.label}</div>
                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{totalProducts>0?Math.round(s.value/totalProducts*100):0}% of catalog</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ PRODUCTS ══ */}
      {tab === "products" && (
        <div>
          {/* Filters */}
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
            <input style={{ ...S.input, width:220 }} placeholder="🔍 Search product or SKU…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{ ...S.input, width:"auto" }} value={filterStat} onChange={e=>setFilterStat(e.target.value)}>
              <option value="all">All Status</option>
              <option value="ok">In Stock</option>
              <option value="low">Low Stock</option>
              <option value="out">Out of Stock</option>
            </select>
            <select style={{ ...S.input, width:"auto" }} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
              <option value="name">Sort: Name</option>
              <option value="stock">Sort: Stock (High)</option>
              <option value="alert">Sort: Stock (Low)</option>
              <option value="price">Sort: Price</option>
            </select>
            <span style={{ fontSize:11, color:"#9ca3af" }}>{filtered.length} products</span>
            {bulkMode && selected.length > 0 && (
              <div style={{ display:"flex", gap:8, marginLeft:"auto", alignItems:"center" }}>
                <span style={{ fontSize:11, fontWeight:600, color:"#F47920" }}>{selected.length} selected</span>
                <input type="number" style={{ ...S.input, width:80 }} placeholder="Set qty"
                  onKeyDown={e => { if(e.key==="Enter") bulkSetAll(e.target.value); }}/>
                <button style={S.btn()} onClick={() => setSelected([])}>Clear</button>
              </div>
            )}
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {bulkMode && <th style={S.th}><input type="checkbox" onChange={e => setSelected(e.target.checked ? filtered.map(p=>p.id) : [])}/></th>}
                    <th style={S.th}>Product</th>
                    <th style={S.th}>SKU</th>
                    {PLATFORMS.map(pl => <th key={pl.id} style={{ ...S.th, color:pl.color }}>{pl.name}</th>)}
                    <th style={S.th}>Total</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Price</th>
                    <th style={S.th}>Set All</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={10} style={{ padding:0 }}>
                      <EmptyState
                        icon="📦"
                        title="No products yet"
                        subtitle="Connect a store or upload your catalog, and your products show up here with stock levels across every platform."
                        columns={["SKU","Product","Stock","Low Alert","Platforms"]}
                        exampleRows={[
                          ["AT_03_BLACK_36","Auto-Lock Belt — Black 36","42","5","Shopify, Myntra"],
                          ["REV_01_BROWN_34","Reversible Belt — Brown 34","8","5","Myntra, Amazon"],
                          ["BR_02_TAN_38","Braided Belt — Tan 38","0","10","Flipkart"],
                        ]}
                      />
                    </td></tr>
                  ) : filtered.map((p, i) => {
                    const st    = stockStatus(p.inventory, p.lowAlert);
                    const total = totalStock(p.inventory);
                    return (
                      <tr key={p.id} style={{ background:i%2===0?"#fff":"#fafafa" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#fff8f2"}
                        onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                        {bulkMode && (
                          <td style={S.td}>
                            <input type="checkbox" checked={selected.includes(p.id)}
                              onChange={e => setSelected(prev => e.target.checked ? [...prev,p.id] : prev.filter(id=>id!==p.id))}/>
                          </td>
                        )}
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            {p.imageUrl
                              ? <img src={p.imageUrl} alt="" style={{ width:32, height:32, borderRadius:6, objectFit:"cover" }}/>
                              : <div style={{ width:32, height:32, borderRadius:6, background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>📦</div>
                            }
                            <div>
                              <div style={{ fontWeight:500, fontSize:12, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                              <div style={{ fontSize:10, color:"#9ca3af" }}>Alert at {p.lowAlert||5} units</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{p.shopifySku}</td>
                        {PLATFORMS.map(pl => {
                          const qty = p.inventory?.[pl.id] ?? 0;
                          return (
                            <td key={pl.id} style={S.td}>
                              <input type="number" min="0"
                                style={S.qtyInput(qty, p.lowAlert)}
                                defaultValue={qty}
                                onBlur={e => updateInv(p.id, pl.id, e.target.value)}
                                onKeyDown={e => { if(e.key==="Enter") e.target.blur(); }}/>
                            </td>
                          );
                        })}
                        <td style={{ ...S.td, fontWeight:700, color:total===0?"#dc2626":total<=(p.lowAlert||5)?"#d97706":"#F47920" }}>{total}</td>
                        <td style={S.td}><span style={{ background:st.bg, color:st.color, fontSize:9, padding:"2px 7px", borderRadius:10, fontWeight:700 }}>{st.label}</span></td>
                        <td style={{ ...S.td, fontWeight:500 }}>{INR(p.price)}</td>
                        <td style={S.td}>
                          <input type="number" min="0" placeholder="Set" style={{ ...S.input, width:56, textAlign:"center" }}
                            onKeyDown={e => { if(e.key==="Enter"&&e.target.value) { PLATFORMS.forEach(pl=>updateInv(p.id,pl.id,e.target.value)); e.target.value=""; toast("Stock updated ✓","success"); }}}
                            onBlur={e => { if(e.target.value) { PLATFORMS.forEach(pl=>updateInv(p.id,pl.id,e.target.value)); e.target.value=""; toast("Stock updated ✓","success"); }}}/>
                        </td>
                        <td style={{ ...S.td, textAlign:"center" }}>
                          <button style={{ ...S.btn("#5a8a00"), padding:"3px 8px", fontSize:10 }}
                            onClick={()=>pushToShopify(p)} title="Push this product's stock to Shopify">
                            ⬆
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ WAREHOUSE ══ */}
      {tab === "warehouse" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:14 }}>
            {[
              { label:"Total SKUs", value:totalProducts, icon:"📦", color:"#2563eb" },
              { label:"Total Units", value:products.reduce((a,p)=>a+totalStock(p.inventory),0).toLocaleString("en-IN"), icon:"📊", color:"#15803d" },
              { label:"Inventory Value", value:INR(totalValue), icon:"💰", color:"#7e22ce" },
            ].map(k => (
              <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:28 }}>{k.icon}</span>
                <div>
                  <div style={{ fontSize:10, color:"#9ca3af", fontWeight:600 }}>{k.label}</div>
                  <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", justifyContent:"space-between" }}>
              <span>🏭 Warehouse Stock Report</span>
              <button style={S.btn("#15803d")} onClick={() => {
                const rows = [["Product","SKU","Shopify","Amazon","Flipkart","Myntra","Meesho","Total","Value"]];
                filtered.forEach(p => rows.push([p.name,p.shopifySku,...PLATFORMS.map(pl=>p.inventory?.[pl.id]||0),totalStock(p.inventory),(p.price||0)*totalStock(p.inventory)]));
                const csv = rows.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
                const a = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download="warehouse_report.csv"; a.click();
                toast("Report downloaded ✓","success");
              }}>⬇ Export CSV</button>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {["Product","SKU",...PLATFORMS.map(p=>p.name),"Total","Value","Status"].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p,i) => {
                    const total = totalStock(p.inventory);
                    const value = (p.price||0) * total;
                    const st    = stockStatus(p.inventory, p.lowAlert);
                    return (
                      <tr key={p.id} style={{ background:i%2===0?"#fff":"#fafafa" }}>
                        <td style={{ ...S.td, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:500 }}>{p.name}</td>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{p.shopifySku}</td>
                        {PLATFORMS.map(pl => (
                          <td key={pl.id} style={{ ...S.td, textAlign:"center", fontWeight:600, color:((p.inventory?.[pl.id]||0)===0)?"#dc2626":((p.inventory?.[pl.id]||0)<=(p.lowAlert||5))?"#d97706":"#374151" }}>
                            {p.inventory?.[pl.id]||0}
                          </td>
                        ))}
                        <td style={{ ...S.td, fontWeight:700, color:"#F47920", textAlign:"center" }}>{total}</td>
                        <td style={{ ...S.td, fontWeight:500 }}>{INR(value)}</td>
                        <td style={S.td}><span style={{ background:st.bg, color:st.color, fontSize:9, padding:"2px 7px", borderRadius:10, fontWeight:700 }}>{st.label}</span></td>
                        <td style={{ ...S.td, textAlign:"center" }}>
                          <button style={{ ...S.btn("#5a8a00"), padding:"3px 8px", fontSize:10 }}
                            onClick={()=>pushToShopify(p)} title="Push this product's stock to Shopify">
                            ⬆
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══ AI REORDER ══ */}
      {tab === "reorder" && (
        <div>
          <div style={{ ...S.card, padding:"16px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>🤖 AI Reorder Suggestions</div>
                <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>AI analyzes your stock levels and suggests optimal reorder quantities</div>
              </div>
              <button style={{ ...S.btn("#6366f1"), opacity:aiLoading?0.7:1 }} onClick={generateReorder} disabled={aiLoading}>
                {aiLoading ? "🤖 Analyzing…" : "🤖 Generate Suggestions"}
              </button>
            </div>

            {/* Low stock items preview */}
            <div style={{ background:"#f9fafb", borderRadius:8, padding:12, marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:600, color:"#6b7280", marginBottom:8 }}>Products that need attention ({products.filter(p=>totalStock(p.inventory)<=(p.lowAlert||5)*2).length}):</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {products.filter(p=>totalStock(p.inventory)<=(p.lowAlert||5)*2).slice(0,10).map(p => (
                  <span key={p.id} style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:6, padding:"3px 8px", fontSize:10, color:"#374151" }}>
                    {p.shopifySku} ({totalStock(p.inventory)} left)
                  </span>
                ))}
              </div>
            </div>
          </div>

          {aiSuggestions.length > 0 && (
            <div style={{ ...S.card, overflow:"hidden" }}>
              <div style={S.hdr}><span>📋 Reorder Suggestions</span><span style={{ fontSize:11, color:"#9ca3af" }}>{aiSuggestions.length} items</span></div>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {["SKU","Product","Current Stock","Suggested Reorder","Priority","Reason"].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {aiSuggestions.map((s,i) => {
                    const prod    = products.find(p=>p.shopifySku===s.sku);
                    const current = prod ? totalStock(prod.inventory) : "—";
                    const pColors = { High:"#dc2626", Medium:"#d97706", Low:"#15803d" };
                    const pBgs    = { High:"#fef2f2", Medium:"#fffbeb", Low:"#f0fdf4" };
                    return (
                      <tr key={i} style={{ background:i%2===0?"#fff":"#fafafa" }}>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{s.sku}</td>
                        <td style={{ ...S.td, maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</td>
                        <td style={{ ...S.td, fontWeight:600, textAlign:"center", color:typeof current==="number"&&current<=5?"#dc2626":"#374151" }}>{current}</td>
                        <td style={{ ...S.td, fontWeight:700, textAlign:"center", color:"#2563eb" }}>{s.reorderQty}</td>
                        <td style={S.td}><span style={{ background:pBgs[s.priority]||"#f3f4f6", color:pColors[s.priority]||"#6b7280", fontSize:10, padding:"2px 8px", borderRadius:10, fontWeight:700 }}>{s.priority}</span></td>
                        <td style={{ ...S.td, color:"#6b7280", fontSize:11 }}>{s.reason}</td>
                        <td style={{ ...S.td, textAlign:"center" }}>
                          <button style={{ ...S.btn("#5a8a00"), padding:"3px 8px", fontSize:10 }}
                            onClick={()=>pushToShopify(p)} title="Push this product's stock to Shopify">
                            ⬆
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding:"12px 16px", borderTop:"1px solid #e5e7eb" }}>
                <button style={S.btn("#15803d")} onClick={() => {
                  const csv = ["SKU,Product,Reorder Qty,Priority,Reason",...aiSuggestions.map(s=>`"${s.sku}","${s.name}",${s.reorderQty},${s.priority},"${s.reason}"`)].join("\n");
                  const a = document.createElement("a"); a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv); a.download="reorder_suggestions.csv"; a.click();
                  toast("Exported ✓","success");
                }}>⬇ Export as Purchase Order</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ SKU MAP ══ */}
      {tab === "skumap" && (
        <div>
          <div style={{ background:"#eff6ff", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:12, color:"#1e40af" }}>
            💡 Map your Shopify SKUs to Amazon, Flipkart, Myntra and Meesho SKUs so inventory syncs correctly across platforms.
          </div>
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {["Product","Shopify SKU","Amazon SKU","Flipkart SKU","Myntra SKU","Meesho SKU",""].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p,i) => {
                    const edits   = skuEdits[p.shopifySku] || {};
                    const map     = skuMap[p.shopifySku]   || {};
                    const isDirty = Object.keys(edits).length > 0;
                    return (
                      <tr key={p.id} style={{ background:i%2===0?"#fff":"#fafafa" }}>
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            {p.imageUrl
                              ? <img src={p.imageUrl} alt="" style={{ width:28,height:28,borderRadius:5,objectFit:"cover" }}/>
                              : <div style={{ width:28,height:28,borderRadius:5,background:"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13 }}>📦</div>
                            }
                            <span style={{ fontWeight:500, fontSize:12, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
                          </div>
                        </td>
                        <td style={S.td}><span style={{ fontFamily:"monospace",fontSize:11,background:"#f3f4f6",padding:"2px 8px",borderRadius:5 }}>{p.shopifySku}</span></td>
                        {["amazonSku","flipkartSku","myntraSku","meeshoSku"].map(field => (
                          <td key={field} style={S.td}>
                            <input style={{ ...S.input, width:120 }} placeholder="Enter SKU"
                              value={edits[field]!==undefined ? edits[field] : (map[field]||"")}
                              onChange={e => setSkuEdits(prev => ({ ...prev, [p.shopifySku]:{ ...(prev[p.shopifySku]||{}), [field]:e.target.value } }))}/>
                          </td>
                        ))}
                        <td style={S.td}>
                          <button style={S.btn(isDirty?"#F47920":"#9ca3af")} onClick={() => saveSkuMap(p.shopifySku)}>
                            {isDirty?"Save ✓":"Saved"}
                          </button>
                        </td>
                        <td style={{ ...S.td, textAlign:"center" }}>
                          <button style={{ ...S.btn("#5a8a00"), padding:"3px 8px", fontSize:10 }}
                            onClick={()=>pushToShopify(p)} title="Push this product's stock to Shopify">
                            ⬆
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
